import type { SortDirection, SortType, ColumnType } from "./types";
/**
 * This is an extended compareFn for sort(...)
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
 * https://www.w3schools.com/jsref/jsref_sort.asp
 *
 * @param argA First argument
 * @param argB Second argument
 * @param type Sorting direction
 * @returns negative/zero/positive value that is used by .sort(...) function
 */
export const stringSorting = ({
  argA,
  argB,
  direction,
}: {
  argA: string;
  argB: string;
  direction: SortDirection;
}) => {
  if (argA.toLocaleLowerCase() < argB.toLocaleLowerCase())
    return direction === "ASC" ? -1 : 1;
  if (argA.toLocaleLowerCase() > argB.toLocaleLowerCase())
    return direction === "ASC" ? 1 : -1;
  return 0;
};

export const numberSorting = ({
  argA,
  argB,
  direction,
}: {
  argA: number;
  argB: number;
  direction: SortDirection;
}) => (direction === "ASC" ? argA - argB : argB - argA);

/**
 * The function determines based on which column the data should be sorted.
 * It compares relevant data properties based on the column in the table.
 *
 * @param sorting Takes sorting type that consists of column name and direction
 * @returns negative/zero/positive value that is used by .sort(...) function
 */
export const dataSorting = ({
  sorting,
  columns,
}: {
  sorting: SortType;
  columns: Array<ColumnType>;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (rowA: any, rowB: any) => {
    const cellA = Object.values(rowA)[sorting.index];
    const cellB = Object.values(rowB)[sorting.index];

    if (columns[sorting.index]?.customSorting !== undefined) {
      return columns[sorting.index].customSorting?.({
        a: cellA,
        b: cellB,
        direction: sorting.direction,
      }) as number;
    } else if (typeof cellA === "number" && typeof cellB === "number") {
      return numberSorting({
        argA: cellA,
        argB: cellB,
        direction: sorting.direction,
      });
    } else if (typeof cellA === "string" && typeof cellB === "string") {
      return stringSorting({
        argA: cellA,
        argB: cellB,
        direction: sorting.direction,
      });
    } else {
      return stringSorting({
        argA: JSON.stringify(cellA),
        argB: JSON.stringify(cellB),
        direction: sorting.direction,
      });
    }
  };
};
